import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  normalizeCurrencyCode,
} from '../constants/currencies';
import {
  hasCurrencyModule,
  hasInvoicePipeline,
  resolveOrgProductModules,
  PRODUCT_MODULE_INVOICE,
} from '../constants/productModule';

/**
 * Fetches the current user's profile, organization, and settings from Firestore.
 * Organization profile prefers `organizationSettings` so all members share the header;
 * falls back to `userSettings` for migration.
 * @param {object} user - Firebase auth user (from useAuth())
 */
export function useUserProfile(user) {
  const [organizationId, setOrganizationId] = useState(null);
  const [organizationName, setOrganizationName] = useState(null);
  const [productModule, setProductModule] = useState(PRODUCT_MODULE_INVOICE);
  const [productModules, setProductModules] = useState([PRODUCT_MODULE_INVOICE]);
  const [currencyModuleEnabled, setCurrencyModuleEnabled] = useState(false);
  const [localCurrency, setLocalCurrency] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [activeYear, setActiveYear] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [companyNumber, setCompanyNumber] = useState(null);
  const [businessCountry, setBusinessCountry] = useState(null);
  const [role, setRole] = useState('member');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setOrganizationId(null);
      setOrganizationName(null);
      setLogoUrl(null);
      setActiveYear(null);
      setCompanyName(null);
      setCompanyNumber(null);
      setBusinessCountry(null);
      setRole('member');
      setProductModule(PRODUCT_MODULE_INVOICE);
      setProductModules([PRODUCT_MODULE_INVOICE]);
      setCurrencyModuleEnabled(false);
      setLocalCurrency(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      getDoc(doc(db, 'users', user.uid)),
      getDoc(doc(db, 'userSettings', user.uid)),
    ])
      .then(async ([userSnap, settingsSnap]) => {
        if (cancelled) return;

        const userData = userSnap.exists() ? userSnap.data() : {};
        setRole(userData.role ?? 'member');
        const orgId = userData.organizationId ?? null;
        setOrganizationId(orgId ?? null);

        let orgWideData = {};
        if (orgId) {
          const orgSettingsSnap = await getDoc(doc(db, 'organizationSettings', orgId));
          if (cancelled) return;
          orgWideData = orgSettingsSnap.exists() ? orgSettingsSnap.data() : {};
        }

        const settingsData = settingsSnap.exists() ? settingsSnap.data() : {};

        const pickLogo =
          String(orgWideData.logoUrl || '').trim() ||
          String(settingsData.logoUrl || '').trim() ||
          null;
        const pickYear =
          String(orgWideData.activeYear || '').trim() ||
          String(settingsData.activeYear || '').trim() ||
          null;
        const names =
          Array.isArray(orgWideData.companyNames) && orgWideData.companyNames.some((x) => String(x || '').trim())
            ? orgWideData.companyNames
            : settingsData.companyNames || [];
        const numbers =
          Array.isArray(orgWideData.companyNumbers) && orgWideData.companyNumbers.some((x) => String(x || '').trim())
            ? orgWideData.companyNumbers
            : settingsData.companyNumbers || [];
        const country =
          String(orgWideData.businessCountry || '').trim() ||
          settingsData.businessCountry ||
          null;
        const pickLocalCurrency =
          normalizeCurrencyCode(orgWideData.localCurrency) ||
          normalizeCurrencyCode(settingsData.localCurrency) ||
          null;

        setLogoUrl(pickLogo);
        setActiveYear(pickYear);
        setCompanyName(names.length > 0 ? names[0] : null);
        setCompanyNumber(numbers.length > 0 ? numbers[0] : null);
        setBusinessCountry(country);
        setLocalCurrency(pickLocalCurrency);

        if (!orgId) {
          setOrganizationName(null);
          setProductModule(PRODUCT_MODULE_INVOICE);
          setProductModules([PRODUCT_MODULE_INVOICE]);
          setCurrencyModuleEnabled(false);
          return;
        }

        const orgSnap = await getDoc(doc(db, 'organizations', orgId));
        if (cancelled) return;
        const orgData = orgSnap.exists() ? orgSnap.data() : {};
        const name = orgData.companyName || orgData.name || null;
        setOrganizationName(name);
        const mods = resolveOrgProductModules(orgData);
        setProductModules(mods);
        setProductModule(mods.includes('sanctionsmodule') && !mods.includes('invoicemodule') ? 'sanctionsmodule' : 'invoicemodule');
        setCurrencyModuleEnabled(hasCurrencyModule(orgData));
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [user?.uid]);

  return {
    organizationId,
    organizationName,
    logoUrl,
    activeYear,
    companyName,
    companyNumber,
    businessCountry,
    localCurrency,
    role,
    productModule,
    productModules,
    currencyModuleEnabled: currencyModuleEnabled && hasInvoicePipeline({ productModules }),
    hasInvoicePipeline: hasInvoicePipeline({ productModules }),
    loading,
    error,
  };
}
